import { site } from '@panel/commons/helpers';
import * as yup from 'yup';

const MAX_PRICE = Number.MAX_SAFE_INTEGER;
const MAX_PRICE_EXPENSES = 1000000000;
const MAX_PRICE_IPTU = 1000000000;
const MIN_PRICE = 1;
const MIN_PRICE_PORTAL_ALLOW_ZERO = 0;
// Peru tiene su propia validacion para varios precios
const portalsAllowZeroPrice = ['ZPAR'];
const allowZeroValue = portalsAllowZeroPrice.includes(site.id);

const priceCommon = yup.object({
  rent: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('2')
        ? schema
            .required('professionalPubli.price.op.rent.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.rent.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(allowZeroValue ? MIN_PRICE_PORTAL_ALLOW_ZERO : MIN_PRICE, 'professionalPubli.price.op.rent.asist.2')
            .max(MAX_PRICE, 'professionalPubli.price.op.rent.asist.2')
        : schema
    ),
  sell: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('1')
        ? schema
            .required('professionalPubli.price.op.sell.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.sell.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(allowZeroValue ? MIN_PRICE_PORTAL_ALLOW_ZERO : MIN_PRICE, 'professionalPubli.price.op.sell.asist.2')
            .max(MAX_PRICE, 'professionalPubli.price.op.sell.asist.2')
        : schema
    ),
  transfer: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('3')
        ? schema
            .required('professionalPubli.price.op.transfer.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.transfer.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(
              allowZeroValue ? MIN_PRICE_PORTAL_ALLOW_ZERO : MIN_PRICE,
              'professionalPubli.price.op.transfer.asist.2'
            )
            .max(MAX_PRICE, 'professionalPubli.price.op.transfer.asist.2')
        : schema
    ),
  temp: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('4')
        ? schema
            .required('professionalPubli.price.op.temp.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.temp.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(allowZeroValue ? MIN_PRICE_PORTAL_ALLOW_ZERO : MIN_PRICE, 'professionalPubli.price.op.temp.asist.2')
            .max(MAX_PRICE, 'professionalPubli.price.op.temp.asist.2')
        : schema
    ),
  expenses: yup
    .mixed()
    .transform((value, originalValue) => {
      // Si es string, lo limpiamos y validamos como integer
      if (typeof originalValue === 'string') {
        const cleanValue = originalValue.replace(/[,.]/g, '');
        const parsedValue = parseInt(cleanValue, 10);

        // Si el valor parseado es válido y no excede el límite, retornamos el original
        if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= MAX_PRICE) {
          return originalValue;
        }
        return undefined;
      }
      return originalValue;
    })
    .nullable()
    .test('is-valid-number', 'professionalPubli.price.expenses.asist.1', value => {
      if (value === null || value === undefined || value === '') return true;

      const cleanValue = String(value).replace(/[,.]/g, '');
      const parsedValue = parseInt(cleanValue, 10);
      return !isNaN(parsedValue) && parsedValue >= 0;
    })
    .test('max-value', 'professionalPubli.price.expenses.asist.2', value => {
      if (value === null || value === undefined || value === '') return true;

      const cleanValue = String(value).replace(/[,.]/g, '');
      const parsedValue = parseInt(cleanValue, 10);
      return parsedValue <= MAX_PRICE_EXPENSES;
    })
});

const iptuField = yup
  .mixed()
  .transform((value, originalValue) => {
    // Si es string, lo limpiamos y validamos como integer
    if (typeof originalValue === 'string') {
      const cleanValue = originalValue.replace(/[,.]/g, '');
      const parsedValue = parseInt(cleanValue, 10);

      // Si el valor parseado es válido y no excede el límite, retornamos el original
      if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= MAX_PRICE) {
        return originalValue;
      }
      return undefined;
    }
    return originalValue;
  })
  .nullable()
  .test('is-valid-number', 'professionalPubli.price.IPTU.error', value => {
    if (value === null || value === undefined || value === '') return true;

    const cleanValue = String(value).replace(/[,.]/g, '');
    const parsedValue = parseInt(cleanValue, 10);
    return !isNaN(parsedValue) && parsedValue >= 0;
  })
  .test('max-value', 'professionalPubli.price.IPTU.error', value => {
    if (value === null || value === undefined || value === '') return true;

    const cleanValue = String(value).replace(/[,.]/g, '');
    const parsedValue = parseInt(cleanValue, 10);
    return parsedValue <= MAX_PRICE_IPTU;
  });

// Validaciones específicas para portales brasileños con IPTU
const priceWithIPTU = priceCommon.shape({
  IPTU: iptuField
});

// Validaciones específicas para portales que requieren campos *-second
const priceWithSecondFields = yup.object({
  rent: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('2')
        ? schema
            .required('professionalPubli.price.op.rent.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.rent.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(MIN_PRICE_PORTAL_ALLOW_ZERO, 'professionalPubli.price.op.rent.asist.2')
            .max(MAX_PRICE, 'professionalPubli.price.op.rent.asist.2')
        : schema
    ),
  'rent-second': yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .nullable()
    .when(['rent'], (rent, schema) => {
      // Solo aplicar validaciones si existe el campo rent y tiene valor
      if (rent !== undefined && rent !== null) {
        return schema
          .test('is-valid-number', 'professionalPubli.price.op.rent.asist.1', value => {
            // Permitir null, undefined o string vacío
            if (value === null || value === undefined || value === '') return true;
            return !isNaN(value);
          })
          .min(MIN_PRICE_PORTAL_ALLOW_ZERO, 'professionalPubli.price.op.rent.asist.2')
          .max(MAX_PRICE, 'professionalPubli.price.op.rent.asist.2')
          .test('is-less-than-main-price', 'professionalPubli.price.op.rent.asist.2', value => {
            // Permitir null, undefined o string vacío
            if (value === null || value === undefined || value === '') return true;
            // if (value === 0 && rent === 0) return true;
            return value < rent;
          });
      }
      return schema;
    }),
  sell: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('1')
        ? schema
            .required('professionalPubli.price.op.sell.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.sell.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(MIN_PRICE_PORTAL_ALLOW_ZERO, 'professionalPubli.price.op.sell.asist.2')
            .max(MAX_PRICE, 'professionalPubli.price.op.sell.asist.2')
        : schema
    ),
  'sell-second': yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .nullable()
    .when(['sell'], (sell, schema) => {
      // Solo aplicar validaciones si existe el campo sell y tiene valor
      if (sell !== undefined && sell !== null) {
        return schema
          .test('is-valid-number', 'professionalPubli.price.op.sell.asist.1', value => {
            // Permitir null, undefined o string vacío
            if (value === null || value === undefined || value === '') return true;
            return !isNaN(value);
          })
          .min(MIN_PRICE_PORTAL_ALLOW_ZERO, 'professionalPubli.price.op.sell.asist.2')
          .max(MAX_PRICE, 'professionalPubli.price.op.sell.asist.2')
          .test('is-less-than-main-price', 'professionalPubli.price.op.sell.asist.2', value => {
            // Permitir null, undefined o string vacío
            if (value === null || value === undefined || value === '') return true;
            // if (value === 0 && sell === 0) return true;
            return value < sell;
          });
      }
      return schema;
    }),
  temp: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .when('$requiredFields', (requiredFields, schema) =>
      requiredFields.includes('4')
        ? schema
            .required('professionalPubli.price.op.temp.asist.1')
            .test(
              'is-valid-number',
              'professionalPubli.price.op.temp.asist.1',
              value => value !== undefined && !isNaN(value)
            )
            .min(MIN_PRICE_PORTAL_ALLOW_ZERO, 'professionalPubli.price.op.temp.asist.2')
            .max(MAX_PRICE, 'professionalPubli.price.op.temp.asist.2')
        : schema
    ),
  'temp-second': yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const parsedValue = parseInt(originalValue.replace(/[,.]/g, ''), 10);
        return isNaN(parsedValue) ? undefined : parsedValue;
      }
      return value;
    })
    .nullable()
    .when(['temp'], (temp, schema) => {
      // Solo aplicar validaciones si existe el campo temp y tiene valor
      if (temp !== undefined && temp !== null) {
        return schema
          .test('is-valid-number', 'professionalPubli.price.op.temp.asist.1', value => {
            // Permitir null, undefined o string vacío
            if (value === null || value === undefined || value === '') return true;
            return !isNaN(value);
          })
          .min(MIN_PRICE_PORTAL_ALLOW_ZERO, 'professionalPubli.price.op.temp.asist.2')
          .max(MAX_PRICE, 'professionalPubli.price.op.temp.asist.2')
          .test('is-less-than-main-price', 'professionalPubli.price.op.temp.asist.2', value => {
            // Permitir null, undefined o string vacío
            if (value === null || value === undefined || value === '') return true;
            // if (value === 0 && temp === 0) return true;
            return value < temp;
          });
      }
      return schema;
    }),
  expenses: yup
    .mixed()
    .transform((value, originalValue) => {
      // Si es string, lo limpiamos y validamos como integer
      if (typeof originalValue === 'string') {
        const cleanValue = originalValue.replace(/[,.]/g, '');
        const parsedValue = parseInt(cleanValue, 10);

        // Si el valor parseado es válido y no excede el límite, retornamos el original
        if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= MAX_PRICE) {
          return originalValue;
        }
        return undefined;
      }
      return originalValue;
    })
    .nullable()
    .test('is-valid-number', 'professionalPubli.price.expenses.asist.1', value => {
      if (value === null || value === undefined || value === '') return true;

      const cleanValue = String(value).replace(/[,.]/g, '');
      const parsedValue = parseInt(cleanValue, 10);
      return !isNaN(parsedValue) && parsedValue >= 0;
    })
    .test('max-value', 'professionalPubli.price.expenses.asist.2', value => {
      if (value === null || value === undefined || value === '') return true;

      const cleanValue = String(value).replace(/[,.]/g, '');
      const parsedValue = parseInt(cleanValue, 10);
      return parsedValue <= MAX_PRICE_EXPENSES;
    })
});

function getCustomFormValidations(siteId = '') {
  const portalValidations = {
    '24MX': priceCommon,
    VAMX: priceCommon,
    ZPAR: priceCommon,
    IWBR: priceWithIPTU,
    WIBR: priceWithIPTU,
    CMBR: priceWithIPTU,
    ICBR: priceCommon,
    UBPE: priceWithSecondFields,
    DVPE: priceWithSecondFields,
    PLEC: priceCommon,
    CAPA: priceCommon
  };
  return portalValidations[siteId];
}

function getValidationSchema() {
  return getCustomFormValidations(site.id);
}

const PriceStepSchema = getValidationSchema();

export { PriceStepSchema };
